import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import "moment/locale/pt";
import axios from "../../config/axios";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";

moment.locale("pt");

const COLORS = {
  pickup: { bg: "#1976d2", text: "#fff", label: "Recolha" },
  return: { bg: "#e65100", text: "#fff", label: "Devolução" },
};

function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = String(dateStr).split("::")[0];
  const m = moment(d, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m : null;
}

function getCalendarDays(currentMonth) {
  const start = moment(currentMonth).startOf("month").startOf("isoWeek");
  const end = moment(currentMonth).endOf("month").endOf("isoWeek");
  const days = [];
  let day = start.clone();
  while (day.isSameOrBefore(end, "day")) {
    days.push(day.clone());
    day.add(1, "day");
  }
  return days;
}

export default function CalendarView() {
  const history = useHistory();
  const [currentMonth, setCurrentMonth] = useState(moment().startOf("month"));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [view, setView] = useState("month"); // month | week

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [ordersRes, returnsRes] = await Promise.all([
        axios.get("/admin/orders?limit=1000"),
        axios.get("/admin/return-orders?limit=1000"),
      ]);

      const pickups = (ordersRes?.data?.data?.orders || [])
        // Inclui pendentes (processing): são as recolhas novas que a equipa tem de agendar
        .filter(o => o.pickup_date && ["processing","confirmed","completed"].includes(o.status))
        .map(o => ({
          id: o._id,
          type: "pickup",
          date: parseDate(o.pickup_date),
          label: o.user_name || o.email || `Pedido #${o._id}`,
          route: `/admin/orders/${o._id}`,
        }))
        .filter(e => e.date);

      const returns = (returnsRes?.data?.data?.orders || [])
        // Só devoluções com data marcada (antes caíam na data de criação)
        .filter(r => ["processing","confirmed","completed"].includes(r.status) && r.return_date)
        .map(r => ({
          id: r._id,
          type: "return",
          date: parseDate(r.return_date),
          label: r.user_name || r.email || `Dev. #${r._id}`,
          route: `/admin/return-orders/${r._id}`,
        }))
        .filter(e => e.date);

      setEvents([...pickups, ...returns]);
    } catch (e) {
      console.error("CalendarView load error", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const days = getCalendarDays(currentMonth);
  const today = moment();

  const eventsOnDay = (day) =>
    events.filter(e => e.date && e.date.isSame(day, "day"));

  const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  return (
    <div style={{ padding: "16px 24px", fontFamily: "Roboto, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <IconButton size="small" onClick={() => setCurrentMonth(m => m.clone().subtract(1, "month"))}>
            <span className="material-icons">chevron_left</span>
          </IconButton>
          <span style={{ fontSize: 20, fontWeight: 600, minWidth: 200, textAlign: "center", textTransform: "capitalize" }}>
            {currentMonth.format("MMMM YYYY")}
          </span>
          <IconButton size="small" onClick={() => setCurrentMonth(m => m.clone().add(1, "month"))}>
            <span className="material-icons">chevron_right</span>
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => setCurrentMonth(moment().startOf("month"))}>
            Hoje
          </Button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Legend */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {Object.entries(COLORS).map(([type, cfg]) => (
              <div key={type} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#555" }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: cfg.bg }} />
                {cfg.label}
              </div>
            ))}
          </div>
          <ButtonGroup size="small">
            <Button variant={view === "month" ? "contained" : "outlined"} color="primary" onClick={() => setView("month")}>Mês</Button>
            <Button variant={view === "week" ? "contained" : "outlined"} color="primary" onClick={() => setView("week")}>Semana</Button>
          </ButtonGroup>
        </div>
      </div>

      {loadError && !loading && (
        <div style={{ margin: "0 0 16px", padding: "12px 16px", background: "#fdecea", color: "#b71c1c", borderRadius: 6 }}>
          Não foi possível carregar as recolhas e devoluções. Recarrega a página; se o erro continuar, avisa a equipa técnica.
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {view === "month" && (
            <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
              {/* Weekday headers */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "#f5f5f5" }}>
                {weekDays.map(d => (
                  <div key={d} style={{ padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "#666", textAlign: "center", borderRight: "1px solid #e0e0e0" }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                {days.map((day, i) => {
                  const isCurrentMonth = day.isSame(currentMonth, "month");
                  const isToday = day.isSame(today, "day");
                  const dayEvents = eventsOnDay(day);

                  return (
                    <div
                      key={i}
                      style={{
                        minHeight: 90,
                        padding: "6px 8px",
                        borderRight: "1px solid #e0e0e0",
                        borderBottom: "1px solid #e0e0e0",
                        background: isToday ? "#e3f2fd" : isCurrentMonth ? "#fff" : "#fafafa",
                        position: "relative",
                      }}
                    >
                      <div style={{
                        fontSize: 13,
                        fontWeight: isToday ? 700 : 400,
                        color: isToday ? "#1976d2" : isCurrentMonth ? "#333" : "#bbb",
                        marginBottom: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}>
                        {isToday ? (
                          <span style={{ background: "#1976d2", color: "#fff", borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                            {day.date()}
                          </span>
                        ) : day.date()}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {dayEvents.slice(0, 3).map(ev => (
                          <Tooltip key={ev.id} title={`${COLORS[ev.type].label}: ${ev.label}`} arrow>
                            <div
                              onClick={() => history.push(ev.route)}
                              style={{
                                background: COLORS[ev.type].bg,
                                color: COLORS[ev.type].text,
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontSize: 11,
                                cursor: "pointer",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                            >
                              {ev.label}
                            </div>
                          </Tooltip>
                        ))}
                        {dayEvents.length > 3 && (
                          <div style={{ fontSize: 11, color: "#888", paddingLeft: 2 }}>
                            +{dayEvents.length - 3} mais
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "week" && <WeekView currentMonth={currentMonth} events={events} history={history} today={today} />}
        </>
      )}
    </div>
  );
}

function WeekView({ currentMonth, events, history, today }) {
  const [weekStart, setWeekStart] = useState(() => today.clone().startOf("isoWeek"));

  const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "day"));
  const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <IconButton size="small" onClick={() => setWeekStart(w => w.clone().subtract(1, "week"))}>
          <span className="material-icons">chevron_left</span>
        </IconButton>
        <span style={{ fontSize: 14, color: "#555" }}>
          {weekStart.format("D MMM")} – {weekStart.clone().add(6, "day").format("D MMM YYYY")}
        </span>
        <IconButton size="small" onClick={() => setWeekStart(w => w.clone().add(1, "week"))}>
          <span className="material-icons">chevron_right</span>
        </IconButton>
        <Button size="small" variant="outlined" onClick={() => setWeekStart(today.clone().startOf("isoWeek"))}>
          Esta semana
        </Button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
        {days.map((day, i) => {
          const isToday = day.isSame(today, "day");
          const dayEvents = events.filter(e => e.date && e.date.isSame(day, "day"));

          return (
            <div key={i} style={{ borderRight: i < 6 ? "1px solid #e0e0e0" : "none", minHeight: 200 }}>
              <div style={{
                padding: "8px 10px",
                background: isToday ? "#1976d2" : "#f5f5f5",
                color: isToday ? "#fff" : "#555",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "center",
                borderBottom: "1px solid #e0e0e0",
              }}>
                {weekDays[i]}<br />
                <span style={{ fontSize: 18 }}>{day.date()}</span>
              </div>
              <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: 4 }}>
                {dayEvents.map(ev => (
                  <Tooltip key={ev.id} title={ev.label} arrow>
                    <div
                      onClick={() => history.push(ev.route)}
                      style={{
                        background: COLORS[ev.type].bg,
                        color: COLORS[ev.type].text,
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontSize: 12,
                        cursor: "pointer",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 10, opacity: 0.85 }}>{COLORS[ev.type].label}</div>
                      {ev.label}
                    </div>
                  </Tooltip>
                ))}
                {dayEvents.length === 0 && (
                  <div style={{ fontSize: 11, color: "#ccc", textAlign: "center", paddingTop: 8 }}>—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
